import React, { useEffect, useRef, useState } from 'react';
import { PropTypes } from 'prop-types';
import routes from 'utils/routes';
import {
  selectors as ordersSelectors,
  actions as ordersActions,
} from 'state/orders';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { push } from 'connected-react-router';
// styles
import styled, { css } from 'styled-components';
import { media } from 'utils/style';
// components
import { ButtonRound } from '@ontruck/orc';
import { SearchIcon } from 'refactored/ui/icons';
import { selectors as clientSelectors, constants } from 'state/clientConfig';

const SearchBarContainer = styled.div`
  display: flex;
  border: 2px solid var(--grey300);
  margin: 8px;

  ${(props) =>
    props.isFocused &&
    css`
      border-color: var(--grey500);
    `}

  background: var(--white);
  border-radius: 4px;
`;

const IconContainer = styled.div`
  background-color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 40px;
`;

const ClearButton = styled.div`
  width: 100%;
  height: 100%;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  display: flex;
`;

const SearchBar = ({ className }) => {
  const { t } = useTranslation(['Orders']);
  const dispatch = useDispatch();
  const isXpo = useSelector((state) =>
    clientSelectors.getSpecificClientConfig(state, constants.IS_XPO)
  );
  const pathsWithSearchBar = [
    routes.orders.default,
    routes.orders.previous,
    routes.orders.active,
    routes.orders.search,
    `${routes.orders.previous}/`,
    `${routes.orders.active}/`,
    `${routes.orders.search}/`,
    `${routes.orders.recurring}/`,
  ];
  const pathname = window.location.pathname;
  const showSearchBar =
    pathsWithSearchBar.some((path) => pathname.includes(path)) && !isXpo;
  let inputRef = useRef(null);

  const searchText = useSelector(ordersSelectors.getSearchText);
  const showClearButton = pathname === routes.orders.search;

  const [isFocused, setIsFocused] = useState(false);

  const handleChange = (e) => {
    const text = e.target.value;
    if (text === '') {
      dispatch(ordersActions.resetData());
      dispatch(ordersActions.fetchOrders({ type: 'search' }));
    } else {
      const search = {
        text,
        pathname,
      };
      dispatch(ordersActions.searchByText(search));
    }
  };

  const onFocus = () => {
    setIsFocused(true);
    if (pathname !== routes.orders.search) {
      dispatch(ordersActions.resetData());
      dispatch(push(routes.orders.search));
    }
  };

  const onBlur = () => {
    setIsFocused(false);
  };

  const onClear = () => {
    inputRef.current.value = '';
    dispatch(push(routes.orders.default));
  };

  useEffect(() => {
    if (pathname !== routes.orders.search) {
      if (inputRef.current !== null) inputRef.current.value = '';
    }
  }, [pathname]);

  if (!showSearchBar) return <></>;
  return (
    <div className={className}>
      <SearchBarContainer isFocused={isFocused}>
        <IconContainer>
          <SearchIcon />
        </IconContainer>
        <input
          aria-label="Search"
          placeholder={t('search_placeholder')}
          type="text"
          defaultValue={searchText}
          onChange={handleChange}
          onFocus={onFocus}
          onBlur={onBlur}
          ref={inputRef}
        ></input>
        <IconContainer>
          {showClearButton && (
            <ClearButton
              onClick={onClear}
              role="button"
              aria-label="Clear search"
            >
              <ButtonRound skin="primary" size="XS" icon="close" />
            </ClearButton>
          )}
        </IconContainer>
      </SearchBarContainer>
    </div>
  );
};

SearchBar.propTypes = {
  className: PropTypes.string,
};

const regularCSS = css`
  background-color: var(--white);
  margin-right: 8px;
  input {
    flex-grow: 1;
    width: 250px;
    border: none;
    outline: none;
    height: 40px;
    font-size: 16px;
    line-height: 24px;
  }
  @media print {
    display: none;
  }
`;

const mobileCSS = css`
  flex-basis: 100%;
  order: 3;
  margin-right: 0px;
`;

const StyledSearchBar = styled(SearchBar)`
  ${regularCSS}
  ${media.lessThan('small')`${mobileCSS}`}
`;

export default StyledSearchBar;
